import type { KnowledgeBuild, ProfileCollection, TranscriptBatch } from "@/api/client";

export const MAX_PROJECT_PROFILES = 50;
export const MAX_TRANSCRIPT_LINKS = 20;

const DOYIN_HOSTS = new Set([
  "douyin.com",
  "www.douyin.com",
  "m.douyin.com",
  "v.douyin.com",
  "iesdouyin.com",
  "www.iesdouyin.com",
  "v.iesdouyin.com",
]);

export function parseDouyinLinks(value: string) {
  const matches = value.match(/https?:\/\/[^\s<>"'，。！？；、]+/gi) || [];
  const links: string[] = [];
  const seen = new Set<string>();
  let invalid = 0;
  let duplicates = 0;

  for (const match of matches) {
    const candidate = match.replace(/[)\]},.;，。！？；、]+$/g, "");
    try {
      const parsed = new URL(candidate);
      if (parsed.protocol !== "https:" || !DOYIN_HOSTS.has(parsed.hostname.toLowerCase())) {
        invalid += 1;
        continue;
      }
      if (seen.has(parsed.href)) {
        duplicates += 1;
        continue;
      }
      seen.add(parsed.href);
      links.push(parsed.href);
    } catch {
      invalid += 1;
    }
  }

  return { links, detected: matches.length, invalid, duplicates };
}

export function localDateValue(now = new Date()) {
  const offset = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export function formatDate(value?: string | null, includeTime = false) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("zh-CN", {
      timeZone: "Asia/Shanghai",
      year: "numeric",
      month: "short",
      day: "numeric",
      ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export function formatDuration(milliseconds?: number | null) {
  if (!milliseconds) return "刚刚开始";
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < 60) return `${seconds} 秒`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} 分 ${String(seconds % 60).padStart(2, "0")} 秒`;
}

export function humanKnowledgeStatus(status: KnowledgeBuild["status"]) {
  const labels: Record<KnowledgeBuild["status"], string> = {
    queued: "等待运行",
    collecting: "采集作品",
    waiting_user: "等待你验证",
    transcribing: "批量转写",
    building: "整理知识库",
    packaging: "正在交付",
    succeeded: "已完成",
    partial: "部分完成",
    failed: "构建失败",
    cancelled: "已取消",
  };
  return labels[status];
}

export function isKnowledgeTerminal(status?: KnowledgeBuild["status"]) {
  return status === "succeeded" || status === "partial" || status === "failed" || status === "cancelled";
}

export function humanProfileStatus(status?: ProfileCollection["status"]) {
  if (!status) return "未开始";
  const labels: Record<ProfileCollection["status"], string> = {
    queued: "等待运行",
    resolving: "解析主页",
    launching_browser: "启动浏览器",
    collecting: "采集作品",
    waiting_user: "等待你验证",
    succeeded: "采集完成",
    partial: "部分完成",
    failed: "采集失败",
    cancelled: "已取消",
  };
  return labels[status];
}

export function isProfileTerminal(status?: ProfileCollection["status"]) {
  return status === "succeeded" || status === "partial" || status === "failed" || status === "cancelled";
}

export function isTranscriptTerminal(status?: TranscriptBatch["status"]) {
  return status === "succeeded" || status === "partial" || status === "failed";
}

export type StatusTone = "neutral" | "info" | "warning" | "success" | "danger";

export function knowledgeStatusTone(status?: KnowledgeBuild["status"]): StatusTone {
  if (status === "succeeded") return "success";
  if (status === "partial" || status === "waiting_user") return "warning";
  if (status === "failed" || status === "cancelled") return "danger";
  if (status === "transcribing" || status === "building" || status === "packaging") return "info";
  return "neutral";
}

const projectStorageKey = "contentResearchDesk.recentProjects";

export function readRecentProjectIds() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(projectStorageKey) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === "string" && /^kb_[A-Za-z0-9_-]+$/.test(id));
  } catch {
    return [];
  }
}

export function rememberProject(id: string) {
  const all = [id, ...readRecentProjectIds().filter((candidate) => candidate !== id)].slice(0, 24);
  try {
    window.localStorage.setItem(projectStorageKey, JSON.stringify(all));
  } catch {
    // Local storage is a convenience only; no server state is altered.
  }
}

export function forgetProject(id: string) {
  try {
    window.localStorage.setItem(
      projectStorageKey,
      JSON.stringify(readRecentProjectIds().filter((candidate) => candidate !== id)),
    );
  } catch {
    // no-op
  }
}

export function previousKnowledgeActiveId() {
  try {
    const value = JSON.parse(window.localStorage.getItem("douyinKnowledgeBuild.activeJob") || "null");
    return typeof value?.job_id === "string" && /^kb_[A-Za-z0-9_-]+$/.test(value.job_id)
      ? value.job_id
      : null;
  } catch {
    return null;
  }
}
